<template>
  <div>
    <TableBatchInvoiceFilters @filters-updated="filterUpdated" />
    <div class="card">
      <BaseDataTable
        v-if="filterReady"
        multi-select
        :entries="tableData.rows"
        :columns="tableData.columns"
        :settings="settings"
        :loading="$apollo.loading"
        :paginator-info="paginator"
        class="f-12"
        @rows-selected="selectedRows = $event"
        @search="search"
      >
        <template #additional-control-bar-elements>
          <ButtonDropdown caret outline size="sm" class="m-l-5">
            {{ $t('Actions') }}
            <template #dropdown>
              <ul class="base-dropdown-menu" style="display: inline-block; position: static">
                <li :class="{ disabled: !allSelectedRowsHaveInvoices }">
                  <BaseHtmlForm action="/billing/invoices/zip" method="POST">
                    <input
                      v-for="row in selectedRows"
                      :key="row.globalId"
                      type="hidden"
                      name="ids[]"
                      :value="row.localId"
                    />
                    <component
                      v-bind="allSelectedRowsHaveInvoices ? { type: 'submit' } : { href: '#' }"
                      :is="allSelectedRowsHaveInvoices ? 'button' : 'a'"
                      v-tooltip="
                        !allSelectedRowsHaveInvoices &&
                        $t(
                          'Your selection includes invoices that are not ready to download yet. Please change your selection.',
                        )
                      "
                      class="no-spinner"
                    >
                      <i class="fa fa-download m-r-5" aria-hidden="true"></i>
                      {{ $t('Download selected invoices') }}
                    </component>
                  </BaseHtmlForm>
                </li>
              </ul>
            </template>
          </ButtonDropdown>
        </template>
        <template #localId="{ row }">
          <BaseLabel basic class="m-r-5 f-11" :text="`#${row.localId}`" />
        </template>
        <template #billIds="{ row }">
          <CollapsibleLabels
            v-slot="slotProps"
            basic
            label-class="di-block m-r-5 f-11"
            :values="row.billIds"
          >
            <PopoverBill :local-id="parseInt(slotProps.loopValue)" />
          </CollapsibleLabels>
        </template>
        <template #poNumbers="{ row }">
          <CollapsibleLabels
            v-slot="slotProps"
            basic
            label-class="di-block m-r-5 f-11"
            :values="row.poNumbers"
          >
            <span
              class="tw-inline-block tw-max-w-[200px] tw-truncate"
              :title="slotProps.loopValue"
            >
              <BaseLabel
                v-bind="slotProps.propsValue"
                :class="slotProps.labelClassValue"
                :text="slotProps.loopValue"
              />
            </span>
          </CollapsibleLabels>
        </template>
        <template #pdfUrl="{ row }">
          <a
            v-if="row.pdfUrl"
            :href="row.pdfUrl"
            :title="$t('Download invoice as PDF')"
            target="_blank"
            class="c-darkgreen"
          >
            <span class="glyphicon glyphicon-file" aria-hidden="true" />
          </a>
          <span
            v-else
            :title="
              $t('PDF is not available for invoice :invoiceno.', {
                invoiceno: row.localId || '',
              }) + ' '
            "
            data-toggle="tooltip"
            class="c-gray"
          >
            <span class="glyphicon glyphicon-file" aria-hidden="true" />
          </span>
        </template>
        <template #status="{ row }">
          <template v-if="row.isPaid">
            <span class="label label-success ucase f-700">{{ $t('Paid') }}</span>
          </template>
          <template v-else-if="row.isOverDue">
            <span class="label label-default ucase f-700">{{ $t('OverDue') }}</span>
          </template>
          <template v-else>
            <span class="label label-default ucase f-700">{{ $t('Unpaid') }}</span>
          </template>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import gql from 'graphql-tag'

import TableBatchInvoiceFilters from '@/components/TableBatchInvoiceFilters.vue'
import HashMixin from '@/mixins/MethodHash'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { stringToEnum } from '@/utils/tools'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import BaseLabel from '@/components/BaseLabel.vue'
import CollapsibleLabels from '@/components/CollapsibleLabels.vue'
import PopoverBill from '@/components/PopoverBill.vue'

export default {
  name: 'TableBatchInvoice',
  components: {
    CollapsibleLabels,
    BaseLabel,
    TableBatchInvoiceFilters,
    ButtonDropdown,
    BaseHtmlForm,
    BaseDataTable,
    PopoverBill,
  },
  mixins: [HashMixin],
  apollo: {
    data: {
      skip() {
        return !this.filterReady
      },
      query: gql`
        query Invoices(
          $filter: InvoiceFilterInput
          $order: [InvoiceOrderByClause!]
          $search: String!
          $page: Int!
          $count: Int!
        ) {
          data: invoices(
            filter: $filter
            orderBy: $order
            search: $search
            first: $count
            page: $page
          ) {
            paginatorInfo {
              ...PaginatorInfoFragment
              count
              currentPage
              total
            }
            data {
              globalId
              localId
              createdAt
              currencySymbol
              pdfUrl
              isBatched
              isCompleted
              isOverDue
              total {
                inDec
                withVat {
                  inDec
                }
              }
              bills {
                data {
                  globalId
                  localId
                  poNumber
                }
              }
            }
          }
        }
        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      variables() {
        return {
          filter: { ...this.filter },
          order: this.ordering,
          search: this.searchValue,
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
        }
      },
    },
  },
  data() {
    return {
      filter: { isBatched: true },
      action: 'default',
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      orderSettings: { id: 'desc' },
      searchValue: '',
      header: [
        {
          key: 'localId',
          slot: 'localId',
          title: 'Invoice ID',
          sort: 'id',
          class: 'text-left',
        },
        {
          key: 'billIds',
          slot: 'billIds',
          title: 'Bills ID',
          orderAble: false,
          class: 'text-left',
          ignoreClickForRowSelection: true,
        },
        {
          key: 'poNumbers',
          slot: 'poNumbers',
          title: 'PO no.',
          class: 'text-left',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          key: 'pdfUrl',
          slot: 'pdfUrl',
          title: 'Invoice',
          class: 'text-left f-13 nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          key: 'total',
          title: 'Invoice Total',
          type: Number,
          class: 'f-500 f-13',
        },
        {
          key: 'status',
          slot: 'status',
          title: 'Status',
          orderAble: false,
        },
      ],
      loading: true,
      currentUrl: this.getCurrentUrl(),
      selectedRows: {},
    }
  },
  computed: {
    filterReady() {
      return Object.keys(this.filter).length > 0
    },
    moment: () => moment,
    settings() {
      return {
        pageLength: this.paginationSettings.displayPerPage, // Elements per page
        order: this.ordering, // Column ordering
        search: { search: this.searchValue }, // Current search
        displayStart: (this.paginationSettings.page - 1) * this.paginationSettings.displayPerPage, // Page number
        autoWidth: false, // Stops resizing issues from occurring
      }
    },
    rows() {
      if (this?.data?.data) {
        const result = this.data.data

        return result.map((instance) => {
          // make a deep copy
          const parsedInstance = JSON.parse(JSON.stringify(instance))

          parsedInstance.billIds = instance.bills.data.map((entryObj) => String(entryObj.localId))
          parsedInstance.poNumbers = instance.bills.data
            .map((entryObj) => entryObj.poNumber)
            .filter(Boolean)
            .map((poNumber) => String(poNumber))

          const total = instance.total.inDec ? instance.total.inDec : instance.total
          parsedInstance.total =
            parseFloat(total.replace(',', '').replace('.', '')) > 0
              ? `${instance.currencySymbol} ${total}`
              : ''

          parsedInstance.pdfUrl = instance.pdfUrl
          return parsedInstance
        })
      }
      return []
    },
    tableData() {
      return new TableDataHandler({
        rows: this.rows,
        columns: this.header,
      })
    },
    paginator() {
      return this?.data?.paginatorInfo
    },
    totalElements() {
      return this?.data?.paginatorInfo?.total || 0
    },
    ordering() {
      const order = []
      for (const key in this.orderSettings) {
        if (this.orderSettings[key]) {
          order.push({
            field: stringToEnum(key),
            order: stringToEnum(this.orderSettings[key]),
          })
        }
      }
      if (order.length === 0) {
        order.push({
          field: stringToEnum('CREATED_AT'),
          order: stringToEnum('DESC'),
        })
      }
      return order
    },
    allSelectedRowsHaveInvoices() {
      const rows = Object.values(this.selectedRows)

      return rows.length > 0 && rows.every((row) => row.pdfUrl)
    },
  },

  methods: {
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.orderSettings = options.order
      this.searchValue = options.search
    },
    filterUpdated($event) {
      this.currentUrl = this.getCurrentUrl()
      // Since the filter might fire at random, we only update our copy when the new filter is different from ours.
      // We also reset out page to one, since the amount of data we have might change such that there are fewer pages
      // for us to paginate.
      if (this.hash(this.filter) !== this.hash($event)) {
        this.filter = $event
        this.paginationSettings.page = 1
      }
    },
    getCurrentUrl() {
      return encodeURIComponent(window.location.pathname + window.location.search)
    },
  },
}
</script>

<style scoped lang="scss"></style>
